(function () {
  if (customElements.get('vittorise-packaging-selector')) return;

  const ATTRIBUTE_KEYS = Object.freeze({
    mode: 'Vittorise packaging mode',
    mirror: 'Vittorise Mirror Box',
    green: 'Vittorise green boxes',
    gift: 'Vittorise gift wrap',
    ribbons: 'Vittorise gold ribbons',
  });

  const ALLOWED_MODES = new Set(['one_per_jewel', 'single', 'none']);

  function numberInRange(value, minimum, maximum) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return minimum;
    return Math.min(Math.max(parsed, minimum), maximum);
  }

  function normalizePayload(payload) {
    const count = Math.max(0, Number.parseInt(payload?.count, 10) || 0);
    if (count === 0) {
      return {
        count: 0,
        mode: 'none',
        mirrorCount: 0,
        greenCount: 0,
        gift: false,
        ribbonCount: 0,
        clear: true,
      };
    }

    let mode = ALLOWED_MODES.has(payload?.mode) ? payload.mode : 'one_per_jewel';
    if (count === 1 && mode === 'single') mode = 'one_per_jewel';
    const mirrorCount = mode === 'none' ? 0 : mode === 'single' ? 1 : count;
    const gift = mirrorCount > 0 && Boolean(payload?.gift);
    const ribbonCount = gift ? numberInRange(payload?.ribbonCount, 1, mirrorCount) : 0;

    return {
      count,
      mode,
      mirrorCount,
      greenCount: count,
      gift,
      ribbonCount,
      clear: false,
    };
  }

  function payloadSignature(payload) {
    const normalized = normalizePayload(payload);
    return JSON.stringify([
      normalized.count,
      normalized.mode,
      normalized.mirrorCount,
      normalized.greenCount,
      normalized.gift,
      normalized.ribbonCount,
      normalized.clear,
    ]);
  }

  class VittorisePackagingManager {
    constructor() {
      this.components = new Set();
      this.current = null;
      this.savedSignature = null;
      this.timer = null;
      this.inFlight = false;
      this.state = 'idle';
    }

    register(component) {
      this.components.add(component);
      const initial = component.readInitialPayload();
      const countChanged = !this.current || this.current.count !== initial.count;

      if (countChanged) {
        this.current = initial;
        this.savedSignature = component.initiallySynced ? payloadSignature(initial) : null;
      }

      component.applyPayload(this.current);

      if (payloadSignature(this.current) === this.savedSignature) {
        this.setState('ready');
      } else {
        this.requestSave(this.current, true);
      }
    }

    unregister(component) {
      this.components.delete(component);
    }

    requestSave(payload, immediate) {
      this.current = normalizePayload(payload);
      this.syncComponents();

      if (payloadSignature(this.current) === this.savedSignature) {
        this.setState('ready');
        return;
      }

      this.setState('saving');
      window.clearTimeout(this.timer);
      if (this.inFlight) return;

      this.timer = window.setTimeout(() => this.flush(), immediate ? 0 : 180);
    }

    retry() {
      if (!this.current) return;
      this.savedSignature = null;
      this.requestSave(this.current, true);
    }

    async flush() {
      if (this.inFlight || !this.current) return;

      const snapshot = normalizePayload(this.current);
      const signature = payloadSignature(snapshot);
      if (signature === this.savedSignature) {
        this.setState('ready');
        return;
      }

      const updateUrl = Array.from(this.components).find((component) => component.updateUrl)?.updateUrl;
      if (!updateUrl) {
        this.setState('error');
        return;
      }

      this.inFlight = true;
      this.setState('saving');

      try {
        const response = await fetch(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attributes: this.attributesFor(snapshot) }),
        });

        let result = null;
        try {
          result = await response.json();
        } catch (_error) {
          result = null;
        }

        if (!response.ok || result?.errors) {
          throw new Error('Packaging preferences could not be saved.');
        }

        this.savedSignature = signature;

        if (payloadSignature(this.current) !== signature) {
          this.inFlight = false;
          this.requestSave(this.current, true);
          return;
        }

        this.setState('saved');
      } catch (_error) {
        if (payloadSignature(this.current) !== signature) {
          this.inFlight = false;
          this.requestSave(this.current, true);
          return;
        }
        this.setState('error');
      } finally {
        this.inFlight = false;
      }
    }

    attributesFor(payload) {
      if (payload.clear) {
        return Object.fromEntries(Object.values(ATTRIBUTE_KEYS).map((key) => [key, '']));
      }

      return {
        [ATTRIBUTE_KEYS.mode]: payload.mode,
        [ATTRIBUTE_KEYS.mirror]: String(payload.mirrorCount),
        [ATTRIBUTE_KEYS.green]: String(payload.greenCount),
        [ATTRIBUTE_KEYS.gift]: payload.gift ? 'yes' : 'no',
        [ATTRIBUTE_KEYS.ribbons]: String(payload.ribbonCount),
      };
    }

    syncComponents() {
      this.components.forEach((component) => {
        if (component.isConnected) component.applyPayload(this.current);
      });
    }

    setState(state) {
      this.state = state;
      this.components.forEach((component) => {
        if (component.isConnected) component.applyState(state);
      });
      this.updateCheckoutButtons(state === 'saving' || state === 'error');
    }

    updateCheckoutButtons(blocked) {
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (blocked) {
          if (!Object.prototype.hasOwnProperty.call(button.dataset, 'vtPackagingWasDisabled')) {
            button.dataset.vtPackagingWasDisabled = button.disabled ? 'true' : 'false';
          }
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          return;
        }

        if (Object.prototype.hasOwnProperty.call(button.dataset, 'vtPackagingWasDisabled')) {
          button.disabled = button.dataset.vtPackagingWasDisabled === 'true';
          delete button.dataset.vtPackagingWasDisabled;
          if (!button.disabled) button.removeAttribute('aria-disabled');
        }
      });
    }
  }

  const manager = new VittorisePackagingManager();

  class VittorisePackagingSelector extends HTMLElement {
    constructor() {
      super();
      this.giftErrorVisible = false;
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.vtPackagingBound === 'true') return;
      this.dataset.vtPackagingBound = 'true';
      this.updateUrl = this.dataset.cartUpdateUrl || '';
      this.initiallySynced = this.dataset.initialSynced === 'true';
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      manager.register(this);
    }

    disconnectedCallback() {
      manager.unregister(this);
    }

    readInitialPayload() {
      const count = Math.max(0, Number.parseInt(this.dataset.jewelryCount, 10) || 0);
      const gift = this.dataset.initialGift === 'yes';
      return normalizePayload({
        count,
        mode: this.dataset.initialMode,
        gift,
        ribbonCount: gift ? this.dataset.initialRibbonCount || count : 0,
      });
    }

    onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !manager.current) return;

      if (target.matches('[data-vt-packaging-mode]')) {
        if (target.value === 'none' && manager.current.gift) this.showGiftError();
        else this.clearGiftError();
        manager.requestSave({ ...manager.current, mode: target.value }, false);
      }

      if (target.matches('[data-vt-packaging-gift]')) {
        if (target.checked && manager.current.mirrorCount === 0) {
          target.checked = false;
          this.showGiftError();
          this.applyPayload(manager.current);
          return;
        }

        this.clearGiftError();
        manager.requestSave(
          {
            ...manager.current,
            gift: target.checked,
            ribbonCount: target.checked ? manager.current.mirrorCount : 0,
          },
          false
        );
      }
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element) || !manager.current) return;

      const stepButton = target.closest('[data-vt-ribbon-step]');
      if (stepButton) {
        const step = Number.parseInt(stepButton.dataset.vtRibbonStep, 10) || 0;
        manager.requestSave(
          {
            ...manager.current,
            gift: true,
            ribbonCount: manager.current.ribbonCount + step,
          },
          false
        );
        return;
      }

      if (target.closest('[data-vt-packaging-retry]')) manager.retry();
    }

    applyPayload(payload) {
      const normalized = normalizePayload(payload);
      this.querySelectorAll('[data-vt-packaging-mode]').forEach((input) => {
        input.checked = input.value === normalized.mode;
      });

      const giftInput = this.querySelector('[data-vt-packaging-gift]');
      if (giftInput) {
        giftInput.checked = normalized.gift;
      }

      const giftContainer = this.querySelector('[data-vt-gift-container]');
      if (giftContainer) giftContainer.classList.toggle('is-unavailable', normalized.mirrorCount === 0);

      const giftUnavailable = this.querySelector('[data-vt-gift-unavailable]');
      if (giftUnavailable) giftUnavailable.hidden = !this.giftErrorVisible;

      if (normalized.mirrorCount > 0) this.clearGiftError();

      const ribbonControl = this.querySelector('[data-vt-ribbon-control]');
      if (ribbonControl) ribbonControl.hidden = !normalized.gift || normalized.mirrorCount === 0;

      const ribbonOutput = this.querySelector('[data-vt-ribbon-output]');
      if (ribbonOutput) ribbonOutput.value = String(normalized.ribbonCount);

      this.querySelectorAll('[data-vt-ribbon-step]').forEach((button) => {
        const step = Number.parseInt(button.dataset.vtRibbonStep, 10) || 0;
        button.disabled =
          !normalized.gift ||
          (step < 0 && normalized.ribbonCount <= 1) ||
          (step > 0 && normalized.ribbonCount >= normalized.mirrorCount);
      });

      this.setField('mode', normalized.mode);
      this.setField('mirror', normalized.mirrorCount);
      this.setField('green', normalized.greenCount);
      this.setField('gift', normalized.gift ? 'yes' : 'no');
      this.setField('ribbons', normalized.ribbonCount);
    }

    showGiftError() {
      this.giftErrorVisible = true;
      const giftContainer = this.querySelector('[data-vt-gift-container]');
      const giftInput = this.querySelector('[data-vt-packaging-gift]');
      const giftUnavailable = this.querySelector('[data-vt-gift-unavailable]');
      if (giftContainer) giftContainer.classList.add('has-validation-error');
      if (giftInput) giftInput.setAttribute('aria-invalid', 'true');
      if (giftUnavailable) {
        giftUnavailable.textContent = this.dataset.giftUnavailableText || giftUnavailable.textContent;
        giftUnavailable.hidden = false;
      }
    }

    clearGiftError() {
      this.giftErrorVisible = false;
      const giftContainer = this.querySelector('[data-vt-gift-container]');
      const giftInput = this.querySelector('[data-vt-packaging-gift]');
      const giftUnavailable = this.querySelector('[data-vt-gift-unavailable]');
      if (giftContainer) giftContainer.classList.remove('has-validation-error');
      if (giftInput) giftInput.removeAttribute('aria-invalid');
      if (giftUnavailable) giftUnavailable.hidden = true;
    }

    setField(name, value) {
      const field = this.querySelector(`[data-vt-field="${name}"]`);
      if (field) field.value = String(value);
    }

    applyState(state) {
      this.dataset.state = state;
      this.setAttribute('aria-busy', state === 'saving' ? 'true' : 'false');

      const status = this.querySelector('[data-vt-packaging-status]');
      if (status) {
        if (state === 'saving') status.textContent = this.dataset.savingText || '';
        else if (state === 'saved') status.textContent = this.dataset.savedText || '';
        else if (state === 'error') status.textContent = this.dataset.errorText || '';
        else status.textContent = '';
      }

      const retry = this.querySelector('[data-vt-packaging-retry]');
      if (retry) retry.hidden = state !== 'error';
    }
  }

  customElements.define('vittorise-packaging-selector', VittorisePackagingSelector);
})();
